import { InfiniteData, QueryClient } from "@tanstack/react-query";
import {
  UNITS_FILTER_OPTIONS_KEY,
  UNITS_NEEDS_LOCKBOX_COUNT_KEY
} from "api/units";
import { PaginatedResponse } from "shared/types";
import { Unit } from "types/landlord/address";

interface UnitMagicLinkPatch {
  active?: boolean;
  listed?: boolean;
  feed_zillow_enabled?: boolean;
  feed_zillow_spotlight_enabled?: boolean;
  feed_zillow_variant?: Unit["feed_zillow_variant"];
  feed_apartments_com_enabled?: boolean;
  feed_apartments_com_variant?: Unit["feed_apartments_com_variant"];
  feed_zumper_enabled?: boolean;
  feed_dwellsy_enabled?: boolean;
  feed_uloop_enabled?: boolean;
  needs_lockbox?: boolean;
}

type UnitsPage = PaginatedResponse<Unit>;
type CachedUnits = UnitsPage | InfiniteData<UnitsPage>;

// Every units list (table and card grid) is keyed by its "/units" endpoint path.
const isUnitsListQuery = ({ queryKey }: { queryKey: readonly unknown[] }) =>
  typeof queryKey[0] === "string" && queryKey[0].includes("/units");

const invalidateUnitsLists = (queryClient: QueryClient) =>
  queryClient.invalidateQueries({ predicate: isUnitsListQuery });

// Only listed units can need a lockbox, so listing or unlisting a unit (or
// switching its applications, which can unlist it) moves the menu badge, the
// Units banner and the filter drawer count. The row itself is patched from the
// response, so the lists are left alone.
export const invalidateNeedsLockboxCounts = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: [UNITS_NEEDS_LOCKBOX_COUNT_KEY] });
  queryClient.invalidateQueries({ queryKey: [UNITS_FILTER_OPTIONS_KEY] });
};

// Linking, unlinking or deleting a lockbox changes which units need one: the
// menu badge, the Units banner, the unit rows and the filter drawer count.
export const invalidateNeedsLockbox = (queryClient: QueryClient) => {
  invalidateNeedsLockboxCounts(queryClient);
  invalidateUnitsLists(queryClient);
};

const isInfinite = (data: CachedUnits): data is InfiniteData<UnitsPage> =>
  "pages" in data;

const applyPatch = (unit: Unit, patch: UnitMagicLinkPatch): Unit => {
  const { active, listed, ...feeds } = patch;

  return {
    ...unit,
    ...feeds,
    ...(listed === undefined ? {} : { listed }),
    ...(active === undefined || !unit.magic_link
      ? {}
      : { magic_link: { ...unit.magic_link, active } })
  };
};

const patchItems = (
  items: Unit[],
  unitId: number,
  patch: UnitMagicLinkPatch
) => {
  if (!items.some((unit) => unit.id === unitId)) return items;

  return items.map((unit) =>
    unit.id === unitId ? applyPatch(unit, patch) : unit
  );
};

export const patchUnitInCaches = (
  queryClient: QueryClient,
  unitId: number,
  patch: UnitMagicLinkPatch
) =>
  queryClient.setQueriesData<CachedUnits>(
    { predicate: isUnitsListQuery },
    (data) => {
      if (!data) return data;

      if (isInfinite(data)) {
        let changed = false;
        const pages = data.pages.map((page) => {
          const items = patchItems(page.items ?? [], unitId, patch);
          if (items === page.items) return page;

          changed = true;
          return { ...page, items };
        });

        return changed ? { ...data, pages } : data;
      }

      if (!Array.isArray(data.items)) return data;

      const items = patchItems(data.items, unitId, patch);

      return items === data.items ? data : { ...data, items };
    }
  );
